import React, { useEffect, useState } from "react";
import { API_BASE_URL } from "../../config/apiConfig";
import { useNavigate } from "react-router-dom";
import { FaTrophy, FaFire, FaClipboardCheck, FaClock, FaArrowLeft, FaStar, FaChartLine, FaHistory, FaChartBar } from "react-icons/fa";
import { Activity, Atom, BarChart3, Book, BookOpen, Brain, Briefcase, Building2, Calculator, ChevronDown, ChevronUp, Coffee, Cross, FileText, FunctionSquare, Gamepad2, Globe, Heart, Home, Leaf, Microscope, Monitor, Palette, PenTool, Plus, Settings, Shield, Sprout, Target, TestTube, TreePine, TrendingUp, UsersIcon, Zap } from "lucide-react";

const IndividualChildStatsPage = () => {
    const navigate = useNavigate();

    // Determine if we're viewing from teacher or parent context
    const viewingContext = localStorage.getItem("viewing_context") || "parent";
    const apiBase = viewingContext === "teacher" ? "teachers" : "parents";

    // Teacher nav sets "student_id"; parent nav sets "child_id"
    const user_id = localStorage.getItem("student_id") || localStorage.getItem("child_id");

    const [userData, setUserData] = useState(null);
    const [studyStats, setStudyStats] = useState({ total_hours: 0, streak: 0 });
    const [showAllSubjects, setShowAllSubjects] = useState(false);
    const [examStats, setExamStats] = useState({
        completed_tests: 0,
        average_score: 0,
        tests_trend: "+0",
        score_trend: "+0%",
    });
    const [recentActivity, setRecentActivity] = useState([]);
    const [loading, setLoading] = useState(true);

    // Fetch student/child profile
    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const token = localStorage.getItem("token");
                const response = await fetch(`${API_BASE_URL}/api/${apiBase}/${user_id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const data = await response.json();
                setUserData(data);
            } catch (err) {
                console.error("Error fetching profile:", err);
            }
        };
        fetchProfile();
    }, [user_id, apiBase]);

    // Fetch exams and calculate stats
    useEffect(() => {
        const fetchExams = async () => {
            try {
                const token = localStorage.getItem("token");
                const response = await fetch(`${API_BASE_URL}/api/${apiBase}/analysis/${user_id}`, {
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || "Failed to fetch exam data");
                }

                const exams = await response.json();
                // Trend window: exams in the last 30 days count as the "current" period.
                const cutoffDate = new Date();
                cutoffDate.setDate(cutoffDate.getDate() - 30);
                console.log(exams, user_id)

                setExamStats(calculateStudyStats(exams.data, cutoffDate));
                setRecentActivity(getRecentActivity(exams.data, 4));
                setLoading(false);
            } catch (error) {
                console.error("Error fetching exam data:", error.message);
                setLoading(false);
            }
        };

        fetchExams();
    }, []);

    // Parse subjects from string
    const parseSubjects = (subjectsString) => {
        try {
            return JSON.parse(subjectsString || "[]");
        } catch {
            return [];
        }
    };

    const userSubjects = userData ? parseSubjects(userData.subjects) : [];
    const displayedSubjects = showAllSubjects ? userSubjects : userSubjects.slice(0, 6);

    const subjectIcons = {
        "Physics": Atom,
        "Chemistry": TestTube,
        "Biology": Leaf,
        "Further Mathematics": FunctionSquare,
        "Mathematics": Calculator,
        "Agricultural Science": Sprout,
        "Commerce": TrendingUp,
        "CRS": Cross,
        "Christian Religious Studies (CRS)": Cross,
        "English Language": PenTool,
        "English Studies": Book,
        "Basic Science": Microscope,
        "Basic Technology": Monitor,
        "Social Studies": Globe,
        "Civic Education": UsersIcon,
              "Computer Science": Monitor,
        "Physical and Health Education": Heart,
        "Physical and Health Education (PHE)": Heart,
        "Business Studies": Briefcase,
        "Creative Arts": Palette,
        "Home Economics": Home,
        "Security Education": Shield,
        "Literature": BookOpen,
        "Geography": Globe,
        "History": FileText,
        "Economics": BarChart3,
        "Government": Building2,
        "French": Coffee,
        "Arabic": Book,
        "Hausa": Book,
        "Igbo": Book,
        "Yoruba": Book,
        "Fine Arts": Palette,
        "Music": Activity,
        "Technical Drawing": Target,
        "Auto Mechanics": Gamepad2,
        "Electronics": Zap,
        "Building Construction": Building2,
        "Metal Work": Plus,
        "Wood Work": TreePine,
        "Food and Nutrition": Home,
        "Clothing and Textiles": Home
    };

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const token = localStorage.getItem("token");
                const res = await fetch(`${API_BASE_URL}/api/${apiBase}/stats/${user_id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const data = await res.json();
                setStudyStats(data.stats);
            } catch (error) {
                console.error("Failed to fetch stats:", error);
            }
        };

        fetchStats();
    }, [apiBase, user_id]);

    const timeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffSeconds = Math.floor((now - date) / 1000);

        if (diffSeconds < 60) return `${diffSeconds} seconds ago`;
        if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)} minutes ago`;
        if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)} hours ago`;
        return `${Math.floor(diffSeconds / 86400)} days ago`;
    };

    // Determine whether an answer is correct.
    // The stored `is_correct` flag is unreliable for some exams, so we recompute
    // from the option the student picked vs. the question's real answer when we can.
    // selected_option is "A"-"D"; quiz.correctAnswer is stored as "1"-"4"
    // (some sources may use "A"-"D"), so we normalise both to a letter before comparing.
    const NUM_TO_LETTER = { "1": "A", "2": "B", "3": "C", "4": "D" };
    const isAnswerCorrect = (answer) => {
        const selected = answer?.selected_option;
        const correctAnswer = answer?.quiz?.correctAnswer;
        if (selected != null && correctAnswer != null) {
            const key = String(correctAnswer).trim().toUpperCase();
            const correctLetter = NUM_TO_LETTER[key] || key; // handles "1"-"4" or "A"-"D"
            return String(selected).trim().toUpperCase() === correctLetter;
        }
        // Fall back to the stored flag if the question data isn't available.
        return Number(answer?.is_correct) === 1;
    };

    const calculateStudyStats = (examData, cutoffDate) => {
        console.log(examData)
        const getTestScores = (data) => {
            const testsMap = {};
            data.forEach(answer => {
                const testId = answer.test.id;
                if (!testsMap[testId]) testsMap[testId] = { total: 0, correct: 0 };
                testsMap[testId].total += 1;
                testsMap[testId].correct += isAnswerCorrect(answer) ? 1 : 0;
            });
            return Object.values(testsMap).map(test => Math.round((test.correct / test.total) * 100));
        };

        const allTestIds = new Set(examData.map(answer => answer.test.id));
        const completedTests = allTestIds.size;

        // Overall average across ALL of the student's exams.
        const allScores = getTestScores(examData);
        const averageScore = allScores.length
            ? Math.round(allScores.reduce((a, b) => a + b, 0) / allScores.length)
            : 0;

        // Trend: compare exams in the recent window (>= cutoffDate) against earlier ones.
        const previousExams = examData.filter(answer => new Date(answer.test.createdAt) < cutoffDate);
        const currentExams = examData.filter(answer => new Date(answer.test.createdAt) >= cutoffDate);

        const currentScores = getTestScores(currentExams);
        const previousScores = getTestScores(previousExams);

        const currentAverageScore = currentScores.length
            ? Math.round(currentScores.reduce((a, b) => a + b, 0) / currentScores.length)
            : averageScore;
        const prevAverageScore = previousScores.length
            ? Math.round(previousScores.reduce((a, b) => a + b, 0) / previousScores.length)
            : 0;

        const prevCompletedTests = new Set(previousExams.map(answer => answer.test.id)).size;
        const testsTrend = completedTests - prevCompletedTests;
        const scoreTrend = currentAverageScore - prevAverageScore;

        return {
            completed_tests: completedTests,
            average_score: averageScore,
            tests_trend: testsTrend >= 0 ? `+${testsTrend}` : `${testsTrend}`,
            score_trend: scoreTrend >= 0 ? `+${scoreTrend}%` : `${scoreTrend}%`,
        };
    };

    // Helper to generate recentActivity from exams (regular + JAMB/WAEC).
    const getRecentActivity = (examData, limit = 4) => {
        // Group answers by test.id (promo test ids come through as `promo-<id>`).
        const testsMap = {};
        (examData || []).forEach(answer => {
            if (!answer || !answer.test) return; // skip orphaned answers
            const testId = answer.test.id;
            if (!testsMap[testId]) {
                const isPromo = answer.source === 'promo' || answer.is_promo === 1;
                testsMap[testId] = {
                    isPromo,
                    examType: answer.examType || null,
                    subject: answer.subject,
                    date: answer.test.createdAt,
                    answers: [],
                };
            }
            testsMap[testId].answers.push(answer);
        });

        // Convert to array and calculate score per test
        const testsArray = Object.values(testsMap).map(test => {
            const total = test.answers.length;
            const correct = test.answers.reduce((acc, a) => acc + (isAnswerCorrect(a) ? 1 : 0), 0);
            const score = total ? Math.round((correct / total) * 100) : 0;
            // JAMB/WAEC exams are labelled by exam name; regular tests by subject.
            const label = test.isPromo
                ? (test.examType ? String(test.examType).toUpperCase() : 'EXAM')
                : test.subject;
            return {
                subject: label,
                rawDate: test.date,
                date: timeAgo(test.date),
                score,
                type: test.isPromo ? 'promo' : 'exam',
                answers: test.answers, // kept so the row can open its result page
            };
        });

        // Sort by test date descending and take the most recent 'limit' tests
        const recentTests = testsArray
            .sort((a, b) => new Date(b.rawDate) - new Date(a.rawDate))
            .slice(0, limit);

        return recentTests;
    };

    // Open a recent test's result page (works for regular and promo). Same view
    // the student/child sees, and Back returns to THIS student page (teacher ->
    // /student-stats, parent -> /child-stats), which re-loads the right student
    // from localStorage.
    const openRecentResult = (activity) => {
        const result = (activity.answers || []).map((a) => ({
            subject: a.subject,
            selected_option: a.selected_option,
            examType: a.examType,
            is_correct: a.is_correct,
            question: a.quiz || a.question || {},
        }));
        navigate("/activity-result", {
            state: {
                result,
                returnTo: viewingContext === "teacher" ? "/student-stats" : "/child-stats",
                title: activity.subject ? `${activity.subject} Result` : "Exam Result",
            },
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading stats...</p>
                </div>
            </div>
        );
    }

    const statsArray = [
        {
            label: "Time Studied",
            value: `${studyStats?.total_hours || 0}h`,
            trend: studyStats?.hours_trend || "+0h",
            icon: <FaClock className="w-5 h-5" />,
            color: "from-blue-500 to-blue-600"
        },
        {
            label: "Study Streak",
            value: `${studyStats?.streak || 0} day${(studyStats?.streak || 0) === 1 ? "" : "s"}`,
            trend: studyStats?.streak_trend || "+0",
            icon: <FaFire className="w-5 h-5" />,
            color: "from-orange-500 to-orange-600"
        },
        {
            label: "Exams Completed",
            value: examStats?.completed_tests || 0,
            trend: examStats?.tests_trend || "+0",
            icon: <FaClipboardCheck className="w-5 h-5" />,
            color: "from-green-500 to-green-600"
        },
        {
            label: "Avg Score",
            value: `${examStats?.average_score || 0}%`,
            trend: examStats?.score_trend || "+0%",
            icon: <FaTrophy className="w-5 h-5" />,
            color: "from-purple-500 to-purple-600"
        },
    ];

    const quickActions = [
        {
            id: 1,
            title: "Performance Insights",
            description: "See your strengths and weaknesses",
            icon: <FaChartLine color="#2148C0" />,
            path: "/performance-insights-individual",
            color: "from-blue-500 to-blue-600"
        },
        {
            id: 2,
            title: "Exam History",
            description: "View your past exams",
            icon: <FaHistory color="#2148C0" />,
            path: "/exam-history-individual",
            color: "from-purple-500 to-purple-600"
        },
        {
            id: 3,
            title: "Leaderboard",
            description: "See top performers and rankings",
            icon: <FaTrophy color="#2148C0" />,
            path: "/leaderboard-individual",
            color: "from-orange-500 to-orange-600"
        },
        {
            id: 4,
            title: "Exam Performance",
            description: "View performance for each exam",
            icon: <FaChartBar color="#2148C0" />,
            path: "/exam-performance-individual",
            color: "from-red-500 to-red-600"
        }
    ];


    // Fixed function to render subject icons
    const getSubjectIcon = (subject) => {
        const IconComponent = subjectIcons[subject] || BookOpen;
        return <IconComponent className="w-6 h-6 text-[#2148C0]" />;
    };

    const handleNavigate = (path) => {
        console.log(`Navigating to: ${path}`);
        navigate(`${path}`)
    };


    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto p-4 md:p-6 lg:p-8">
                {/* Header */}
                <div className="flex items-center mb-6">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 text-[#2148C0] hover:text-blue-700 font-medium transition-colors duration-200 bg-white px-4 py-2 rounded-lg shadow-sm hover:shadow-md"
                    >
                        <FaArrowLeft className="text-lg" />
                        Back
                    </button>
                </div>

                {/* Title */}
                <div className="mb-8">
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                        {userData?.firstName} {userData?.lastName}'s Performance Overview
                    </h1>
                    <p className="text-gray-600">Track progress and achievements</p>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
                    {statsArray.map((stat, index) => {
                        const isPositive = stat.trend.startsWith("+");
                        return (
                            <div
                                key={index}
                                className="bg-white p-4 md:p-6 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer transform hover:-translate-y-1 border border-gray-100"
                            >
                                <div className={`w-12 h-12 bg-gradient-to-r ${stat.color} rounded-xl flex items-center justify-center text-white mb-4`}>
                                    {stat.icon}
                                </div>
                                <div className="space-y-1">
                                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                                    <p className="text-sm text-gray-600">{stat.label}</p>
                                    <span className={`inline-block text-xs px-2 py-1 rounded-full font-semibold ${isPositive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                                        }`}>
                                        {stat.trend}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="mb-8">
                    <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                        <Zap className="mr-3 w-6 h-6 text-[#2148C0]" />
                        Quick Actions
                    </h3>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        {quickActions.map((action) => (
                            <div
                                key={action.id}
                                onClick={() => handleNavigate(action.path)}
                                className="group relative bg-white p-6 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-2 border border-gray-100 overflow-hidden"
                            >
                                <div
                                    className={`absolute inset-0 bg-gradient-to-r ${action.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}
                                ></div>
                                <div className="relative z-10 text-center">
                                    <div className="text-4xl mb-4 group-hover:scale-125 transition-transform duration-300">
                                        {action.icon}
                                    </div>
                                    <h4 className="font-bold text-gray-800 group-hover:text-[#2148C0] transition-colors duration-200 mb-2">
                                        {action.title}
                                    </h4>
                                    <p className="text-sm text-gray-600 group-hover:text-gray-700 transition-colors duration-200">
                                        {action.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                </div>

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Subjects Section */}
                    <div className="lg:col-span-2">
                        {userSubjects.length > 0 && (
                            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                                <div className="flex items-center justify-between mb-6">
                                    <h3 className="text-xl font-bold text-gray-800 flex items-center">
                                        <BookOpen className="mr-3 w-6 h-6 text-[#2148C0]" />
                                        {userData?.firstName}'s  Subjects
                                        <span className="ml-3 bg-gradient-to-r from-[#2148C0] to-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                                            {userSubjects.length}
                                        </span>
                                    </h3>
                                    <button className="flex items-center text-sm text-[#2148C0] hover:text-blue-700 font-medium transition-colors duration-200 bg-blue-50 px-3 py-2 rounded-lg hover:bg-blue-100">
                                        <Settings className="mr-2 w-4 h-4" />
                                        Manage
                                    </button>
                                </div>

                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                    {[...displayedSubjects]
                                        .sort((a, b) => a.localeCompare(b))
                                        .map((subject, index) => (
                                            <div
                                                key={index}
                                                className="group bg-gradient-to-br from-gray-50 to-white p-4 rounded-xl border border-gray-100 hover:shadow-lg hover:border-[#2148C0] transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
                                            >
                                                <div className="text-center">
                                                    <div className="mb-3 flex justify-center group-hover:scale-110 transition-transform duration-300">
                                                        {getSubjectIcon(subject)}
                                                    </div>
                                                    <p className="text-sm font-semibold text-gray-700 group-hover:text-[#2148C0] transition-colors duration-200 leading-tight">
                                                        {subject}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                </div>

                                {userSubjects.length > 6 && (
                                    <div className="text-center mt-6">
                                        <button
                                            onClick={() => setShowAllSubjects(!showAllSubjects)}
                                            className="inline-flex items-center text-sm text-[#2148C0] hover:text-blue-700 font-medium transition-colors duration-200 bg-blue-50 px-4 py-2 rounded-lg hover:bg-blue-100"
                                        >
                                            {showAllSubjects ? (
                                                <>
                                                    <ChevronUp className="mr-2 w-4 h-4" />
                                                    Show Less
                                                </>
                                            ) : (
                                                <>
                                                    <ChevronDown className="mr-2 w-4 h-4" />
                                                    Show More ({userSubjects.length - 6} more)
                                                </>
                                            )}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Recent Activity */}
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                                <BarChart3 className="mr-2 w-5 h-5 text-[#2148C0]" />
                                Recent Activity
                            </h3>
                            <div className="space-y-3">
                                {recentActivity.length > 0 ? (
                                    recentActivity.map((activity, index) => (
                                        <button
                                            key={index}
                                            type="button"
                                            onClick={() => openRecentResult(activity)}
                                            className="w-full text-left flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200 cursor-pointer"
                                        >
                                            <div className="flex items-center space-x-3">
                                                <div className={`w-3 h-3 rounded-full ${activity.type === 'promo' ? 'bg-[#2148C0]' : 'bg-red-500'
                                                    }`}></div>
                                                <div>
                                                    <p className="font-medium text-gray-800 text-sm">{activity.subject}</p>
                                                    <p className="text-xs text-gray-600">{activity.date}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <span className={`text-sm font-bold ${activity.score >= 80 ? 'text-green-600' :
                                                    activity.score >= 60 ? 'text-yellow-600' : 'text-red-600'
                                                    }`}>
                                                    {activity.score}%
                                                </span>
                                                <FaStar className={`w-3 h-3 ${activity.score >= 80 ? 'text-yellow-500' : 'text-gray-400'
                                                    }`} />
                                            </div>
                                        </button>
                                    ))
                                ) : (
                                    <div className="text-center py-8">
                                        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                            <BarChart3 className="w-6 h-6 text-gray-400" />
                                        </div>
                                        <p className="text-gray-500 text-sm">No recent activity</p>
                                        <p className="text-gray-400 text-xs">Start taking tests to see your progress</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Study Tip */}
                        <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl border border-yellow-200 p-6">
                            <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center">
                                <Brain className="mr-2 w-5 h-5 text-yellow-600" />
                                💡 Study Tip
                            </h3>
                            <p className="text-gray-700 leading-relaxed text-sm">
                                "Encourage your child to set small, achievable study goals each day and celebrate their progress. Positive reinforcement helps build confidence and makes learning enjoyable!"
                            </p>
                            <div className="mt-4 flex items-center text-xs text-yellow-700">
                                <Coffee className="w-3 h-3 mr-1" />
                                Powered by TeeChaa AI
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default IndividualChildStatsPage;